

























































































































































import {
  defineNuxtComponent,
  computed,
  ref,
  watch
} from '~/utils/nuxt3-migration'
import { useNamespacedStore } from '~/compositions/store'
import { CLASSIFIED_SEARCH_NS } from '~/store/modules/shared/classifieds/search/state'
import { categoriesIconsMappings } from '~/constants/category/icon'
import useClassifiedSearchFacet from '~/compositions/classified-search/useClassifiedSearchFacet'
import { ciAngleLeft } from '~/icons/source/regular/angle-left'
import { ciAngleDown } from '~/icons/source/regular/angle-down'
import ClassifiedSearchCategoryPickerChildCategory from '~/components/car/classifieds/search/facets/ClassifiedSearchCategoryPickerChildCategory.vue'
import CCustomSelect from '~/components/shared/configurable/form/select/custom/CCustomSelect.vue'
import { Searchable } from '~/models/search/types'

export default defineNuxtComponent({
  components: { CCustomSelect, ClassifiedSearchCategoryPickerChildCategory },
  setup() {
    const {
      state: classifiedSearchState,
      getters: classifiedSearchGetters
    } = useNamespacedStore(CLASSIFIED_SEARCH_NS)
    const { changeParam } = useClassifiedSearchFacet({
      onClearAll: () => {
        selectedCategories.value = []
      }
    })

    const categoryFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('category')
    )

    const isMobileModalVisible = ref(false)
    const selectedCategories = ref(categoryFacet.value.values?.selected || [])

    const loadingFacets = computed(() => classifiedSearchState.loading.facets)

    const categoryMap = computed(() => {
      const map = new Map<number, Searchable>()

      const flatten = (categories: Searchable[]) => {
        for (const category of categories) {
          map.set(category.v as number, category)
          if (category.k) {
            flatten(category.k)
          }
        }
      }

      flatten(categoryFacet.value.searchables || [])

      return map
    })

    const selectedCategoriesNames = computed(() => {
      if (!selectedCategories.value?.length) {
        return []
      }

      const selectedIdsSet = new Set(selectedCategories.value)

      return selectedCategories.value
        .filter((selectedId: number) => {
          const category = categoryMap.value.get(selectedId)
          const isLeaf = !category?.k?.length
          if (isLeaf) {
            return true
          }

          const hasSelectedChild = category.k.some((child: Searchable) =>
            selectedIdsSet.has(child.v as number)
          )
          return !hasSelectedChild
        })
        .map((id: number) => categoryMap.value.get(id)?.n)
        .filter(Boolean) as string[]
    })

    const displayedRootCategories = computed(() => {
      if (!categoryFacet.value.searchables?.length) {
        return []
      }

      if (categoryFacet.value.isSelected) {
        const selectedCategory = categoryFacet.value.searchables.find(
          (c: Searchable) => c.s
        )
        return selectedCategory ? [selectedCategory] : []
      }

      return categoryFacet.value.searchables
    })

    watch(categoryFacet, newFacet => {
      selectedCategories.value = newFacet.values?.selected || []
    })

    function onClearButtonClick() {
      changeParam({ name: 'category', value: null })
    }

    function onRootCategoryClick(category: Searchable) {
      if (categoryFacet.value.isSelected) {
        changeParam({ name: 'category', value: null })
      } else {
        changeParam({ name: 'category', value: category.v })
      }
    }

    function getRootCategoryIcon(category: Searchable) {
      return categoryFacet.value.isSelected
        ? ciAngleLeft
        : categoriesIconsMappings[category.v as number]
    }

    function getChildCategoryIds(category: Searchable): number[] {
      const childrenIds: number[] = []

      if (category.k) {
        category.k.forEach((child: Searchable) => {
          childrenIds.push(child.v as number)
          childrenIds.push(...getChildCategoryIds(child))
        })
      }

      return childrenIds
    }

    function onChildCategoryClick(category: Searchable) {
      if (category.s) {
        const childrenIds = getChildCategoryIds(category)
        const idsToRemove = [category.v, ...childrenIds]

        selectedCategories.value = selectedCategories.value.filter(
          (c: number) => !idsToRemove.includes(c)
        )
      } else {
        selectedCategories.value = [
          ...selectedCategories.value,
          Number(category.v)
        ]
      }

      changeParam({
        name: 'category',
        value: selectedCategories.value
      })
    }

    function onMobileSelectClick() {
      isMobileModalVisible.value = true
    }

    function isCategoryDisabled(category: Searchable): boolean {
      return category.c === 0 && !category.s
    }

    return {
      ciAngleDown,
      loadingFacets,
      categoryFacet,
      isMobileModalVisible,
      displayedRootCategories,
      selectedCategories,
      selectedCategoriesNames,
      onClearButtonClick,
      onRootCategoryClick,
      getRootCategoryIcon,
      onChildCategoryClick,
      onMobileSelectClick,
      isCategoryDisabled
    }
  }
})
